<script setup>
import { ref, computed, getCurrentInstance, onMounted, onUnmounted } from 'vue';
import { RouterLink, useRouter } from 'vue-router';

const { proxy } = getCurrentInstance();
const { $stores, $constants, $bus } = proxy;

const router = useRouter();
const loading = computed(() => $stores?.user?.state?.loading || false);

const showPassword = ref(false);
const errorMessage = ref('');

const onLoginSuccess = () => {
  router.push('/dashboard');
};

const onLoginError = (error) => {
  errorMessage.value = error?.data?.message || 'Échec de la connexion. Vérifiez vos identifiants.';
};

const handleLogin = () => {
  errorMessage.value = '';
  const form = document.getElementById('loginForm');
  const formData = new FormData(form);

  const data = {
    email: formData.get('email'),
    password: formData.get('password'),
  };

  if (!data.email || !data.password) {
    errorMessage.value = 'Veuillez remplir tous les champs';
    return;
  }

  proxy.$bus?.emit($constants?.EVENTS.USER.LOGIN, data);
};

onMounted(() => {
  $bus?.on($constants?.EVENTS.USER.LOGIN_SUCCESS, onLoginSuccess);
  $bus?.on($constants?.EVENTS.USER.LOGIN_ERROR, onLoginError);
});

onUnmounted(() => {
  $bus?.off($constants?.EVENTS.USER.LOGIN_SUCCESS, onLoginSuccess);
  $bus?.off($constants?.EVENTS.USER.LOGIN_ERROR, onLoginError);
});
</script>

<template>
  <div class="min-h-screen flex" style="background: #0A0A0B;">
    <!-- Left Side - Branding -->
    <div class="hidden lg:flex lg:w-1/2 relative overflow-hidden">
      <div class="absolute inset-0 bg-gradient-to-br from-[#37b6e9]/20 to-[#4b4ced]/20"></div>
      <div class="absolute inset-0" style="background: radial-gradient(circle at 30% 50%, rgba(55, 182, 233, 0.1) 0%, transparent 50%), radial-gradient(circle at 70% 50%, rgba(75, 76, 237, 0.1) 0%, transparent 50%);"></div>

      <div class="relative z-10 flex flex-col justify-between h-full p-12 text-white">
        <div>
          <RouterLink to="/" class="flex items-center gap-3">
            <div class="w-12 h-12 rounded-xl flex items-center justify-center" style="background: linear-gradient(135deg, #37b6e9 0%, #2e9ac4 100%); box-shadow: 0 4px 15px rgba(55, 182, 233, 0.3);">
              <span class="material-icons-outlined text-white text-xl">store</span>
            </div>
            <div class="flex flex-col">
              <span class="font-bold text-xl" style="font-family: 'Outfit', sans-serif;">Kingrecharges</span>
              <span class="text-xs text-white/50">Premium Store</span>
            </div>
          </RouterLink>
        </div>

        <div class="max-w-lg">
          <h1 class="text-5xl font-black mb-6 leading-tight" style="font-family: 'Outfit', sans-serif;">
            Abonnements premium à des prix imbattables
          </h1>
          <p class="text-white/70 text-lg leading-relaxed">
            Netflix, Spotify, Disney+, Apple TV+ et plus encore. Rejoignez des milliers de clients satisfaits et profitez de nos offres exceptionnelles.
          </p>
        </div>

        <div class="flex items-center gap-8">
          <div>
            <div class="text-3xl font-black" style="font-family: 'Outfit', sans-serif;">50k+</div>
            <div class="text-sm text-white/50">Clients satisfaits</div>
          </div>
          <div>
            <div class="text-3xl font-black" style="font-family: 'Outfit', sans-serif;">24/7</div>
            <div class="text-sm text-white/50">Support client</div>
          </div>
          <div>
            <div class="text-3xl font-black" style="font-family: 'Outfit', sans-serif;">100%</div>
            <div class="text-sm text-white/50">Livraison instantanée</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Right Side - Login Form -->
    <div class="flex-1 flex items-center justify-center p-6 md:p-12">
      <div class="w-full max-w-md">
        <!-- Mobile Logo -->
        <div class="lg:hidden text-center mb-8">
          <RouterLink to="/" class="inline-flex items-center gap-3">
            <div class="w-10 h-10 rounded-xl flex items-center justify-center" style="background: linear-gradient(135deg, #37b6e9 0%, #2e9ac4 100%);">
              <span class="material-icons-outlined text-white">store</span>
            </div>
            <span class="font-bold text-lg text-white" style="font-family: 'Outfit', sans-serif;">Kingrecharges</span>
          </RouterLink>
        </div>

        <div class="mb-8">
          <h1 class="text-3xl font-black text-white mb-2" style="font-family: 'Outfit', sans-serif;">Connexion</h1>
          <p class="text-white/60">Bienvenue sur Kingrecharges</p>
        </div>

        <!-- Error Message -->
        <div v-if="errorMessage" class="mb-6 p-4 rounded-xl" style="background: rgba(239, 68, 68, 0.1); border: 1px solid rgba(239, 68, 68, 0.2);">
          <p class="text-sm text-red-400 flex items-center gap-2">
            <span class="material-icons-outlined text-lg">error</span>
            <span>{{ errorMessage }}</span>
          </p>
        </div>

        <form id="loginForm" @submit.prevent="handleLogin" class="space-y-5">
          <!-- Email -->
          <div>
            <label class="block text-sm font-semibold mb-2 text-white/70">Email</label>
            <div class="relative">
              <span class="absolute left-4 top-1/2 -translate-y-1/2 material-icons-outlined text-white/30">email</span>
              <input
                name="email"
                type="email"
                required
                class="w-full pl-12 pr-4 py-3.5 rounded-xl text-white transition-all"
                style="background: rgba(255, 255, 255, 0.05); border: 1px solid rgba(255, 255, 255, 0.06);"
                placeholder="votre@email.com"
              />
            </div>
          </div>

          <!-- Password -->
          <div>
            <label class="block text-sm font-semibold mb-2 text-white/70">Mot de passe</label>
            <div class="relative">
              <span class="absolute left-4 top-1/2 -translate-y-1/2 material-icons-outlined text-white/30">lock</span>
              <input
                name="password"
                :type="showPassword ? 'text' : 'password'"
                required
                class="w-full pl-12 pr-12 py-3.5 rounded-xl text-white transition-all"
                style="background: rgba(255, 255, 255, 0.05); border: 1px solid rgba(255, 255, 255, 0.06);"
                placeholder="Votre mot de passe"
              />
              <button
                type="button"
                @click="showPassword = !showPassword"
                class="absolute right-4 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors"
              >
                <span class="material-icons-outlined">{{ showPassword ? 'visibility_off' : 'visibility' }}</span>
              </button>
            </div>
          </div>

          <!-- Forgot password -->
          <div class="text-right">
            <button type="button" class="text-sm font-semibold transition-colors" style="color: #37b6e9;">
              Mot de passe oublié ?
            </button>
          </div>

          <!-- Submit button -->
          <button
            type="submit"
            :disabled="loading"
            class="w-full py-4 rounded-xl font-bold text-white transition-all hover:scale-[1.02] hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2"
            style="background: linear-gradient(135deg, #37b6e9 0%, #2e9ac4 100%); box-shadow: 0 4px 20px rgba(55, 182, 233, 0.3);"
          >
            <span v-if="loading" class="material-icons-outlined animate-spin">refresh</span>
            <span v-else>Se connecter</span>
          </button>
        </form>

        <!-- Sign up link -->
        <p class="text-center text-white/60 mt-8">
          Pas encore de compte ?
          <RouterLink to="/register" class="font-semibold hover:underline" style="color: #37b6e9;">Créer un compte</RouterLink>
        </p>

        <!-- Back to home -->
        <div class="text-center mt-6">
          <RouterLink to="/" class="inline-flex items-center gap-2 text-white/50 hover:text-white transition-colors">
            <span class="material-icons-outlined text-lg">arrow_back</span>
            Retour à l'accueil
          </RouterLink>
        </div>
      </div>
    </div>
  </div>
</template>
